/**
 * Form calculations (`dependent_fields[].expression`) are written for mobile, which evaluates them as JavaScript
 * (`new Function`), while the web evaluates them with mathjs. mathjs already reads most of that syntax (arithmetic,
 * comparisons, `? :`, strings, `Math.round(...)` through the scope); it lacks JavaScript's logical and strict
 * operators. This maps those, outside string literals, so one expression works on both:
 *   `&&` -> `and`, `||` -> `or`, `===` -> `==`, `!==` -> `!=`, prefix `!` -> `not` (a postfix `!` stays factorial).
 * `Number(...)` comes from the evaluator's scope. Expressions without these operators are returned unchanged.
 */
export function toMathJsSyntax(expression: string): string {
  if (typeof expression !== 'string' || !/[&|!=]/.test(expression)) return expression;
  let out = '';
  let quote: string | null = null;
  // Last significant (non-space) character outside strings, as written: decides whether `!` is prefix.
  let prev = '';
  for (let i = 0; i < expression.length; i++) {
    const char = expression[i];
    if (quote) {
      out += char;
      if (char === '\\' && i + 1 < expression.length) { out += expression[++i]; continue; }
      if (char === quote) { quote = null; prev = char; }
      continue;
    }
    if (char === '"' || char === '\'') { quote = char; out += char; continue; }
    const next = expression.slice(i, i + 3);
    if (next === '===' || next === '!==') { out += next[0] === '!' ? '!=' : '=='; i += 2; prev = '='; continue; }
    const pair = expression.slice(i, i + 2);
    if (pair === '&&' || pair === '||') { out += pair === '&&' ? ' and ' : ' or '; i += 1; prev = '&'; continue; }
    if (char === '!' && expression[i + 1] !== '=' && (prev === '' || '(,?:=<>&|+-*/%^!'.includes(prev))) {
      out += ' not ';
      prev = '!';
      continue;
    }
    out += char;
    if (char.trim()) prev = char;
  }
  return out;
}
